/**
 * El HUD de medición, que el README exige para cualquier trabajo que toque el
 * render: sin él la regla de oro no es verificable.
 *
 * Los dos contadores que importan:
 *
 *  - `raster:` — rasterizaciones de textura. Panear sobre tarjetas ya cacheadas
 *    no debe moverlo.
 *  - `edges:` — aristas repintadas en el último frame. Panear sobre un diagrama
 *    quieto debe dejarlo en 0; hacer zoom sólo lo mueve al cruzar una banda
 *    (ver `zoomBand` en `edgeGeometry.ts`).
 *
 * Se enciende con `?hud` en la URL o con `localStorage.blanqo:hud = "1"`, así
 * que no cuesta nada en uso normal. Los estilos van inline a propósito: es una
 * herramienta de desarrollo y no tiene por qué entrar al CSS del producto.
 */

export interface HudStats {
  cardsVisible: number;
  cardsTotal: number;
  edgesTotal: number;
  /** Aristas repintadas en el último sync de `EdgeLayer`. */
  edgesRepainted: number;
  zoom: number;
  raster: number;
}

/** ¿Hay que mostrarlo? Ver el comentario de arriba. */
export function hudEnabled(): boolean {
  try {
    if (new URLSearchParams(window.location.search).has("hud")) return true;
    return window.localStorage.getItem("blanqo:hud") === "1";
  } catch {
    // localStorage puede tirar en modo privado; el HUD no vale un crash.
    return false;
  }
}

/** Cada cuánto se escribe al DOM. Un HUD que parpadea a 60 Hz no se lee. */
const REFRESH_MS = 250;

export class DevHud {
  readonly root: HTMLElement;
  private line: HTMLElement;
  private frames = 0;
  private since = performance.now();
  private lastWrite = 0;
  private fps = 0;
  private lastText = "";

  constructor(onStress: () => void) {
    this.root = document.createElement("div");
    // La posición va en el CSS (`.dev-hud`): arriba al centro del área visible,
    // que se corre con la barra lateral igual que la barra de herramientas.
    this.root.className = "dev-hud";
    this.root.style.cssText = [
      "display:flex",
      "gap:8px",
      "align-items:center",
      "padding:6px 8px",
      "border-radius:6px",
      "background:rgba(20,20,22,.82)",
      "color:#e8e8ea",
      "font:11px/1.4 ui-monospace,SFMono-Regular,Menlo,monospace",
      "pointer-events:auto",
      "user-select:none",
    ].join(";");

    this.line = document.createElement("span");
    this.root.appendChild(this.line);

    const btn = document.createElement("button");
    btn.textContent = "stress";
    btn.style.cssText = [
      "font:inherit",
      "color:inherit",
      "background:rgba(255,255,255,.14)",
      "border:0",
      "border-radius:4px",
      "padding:2px 6px",
      "cursor:pointer",
    ].join(";");
    btn.addEventListener("pointerdown", (e) => e.stopPropagation());
    btn.addEventListener("click", onStress);
    this.root.appendChild(btn);
  }

  /** Se llama por frame; sólo escribe al DOM cada `REFRESH_MS`. */
  update(stats: HudStats): void {
    this.frames++;
    const now = performance.now();
    const elapsed = now - this.since;
    if (elapsed >= 500) {
      this.fps = Math.round((this.frames * 1000) / elapsed);
      this.frames = 0;
      this.since = now;
    }
    if (now - this.lastWrite < REFRESH_MS) return;
    this.lastWrite = now;

    const text =
      `${this.fps} fps · ` +
      `cards ${stats.cardsVisible}/${stats.cardsTotal} · ` +
      `edges ${stats.edgesTotal} · ` +
      `zoom ${stats.zoom.toFixed(2)} · ` +
      `raster: ${stats.raster} · ` +
      `repaint: ${stats.edgesRepainted}`;
    if (text !== this.lastText) {
      this.lastText = text;
      this.line.textContent = text;
    }
  }

  destroy(): void {
    this.root.remove();
  }
}
